import { html } from '@shared/utils/rendering/html';
import { useMemo, useRef } from 'preact/hooks';
import { useMutation, useQueryClient } from '@tanstack/react-query';

import Form from '@shared/components/forms';
import Button from '@shared/components/buttons';
import { LoaderWithTitle } from '@shared/components/loading';

import { useCachedFetch } from '@core/utils/hooks/use-cached-fetch';
import { usePut } from '@core/utils/hooks/use-put';
import { UserDetailsFormSchema } from '@core/configs/admin/users/user-details-form.schema';
import { UserDetailsFormOptions } from '@core/configs/admin/users/user-details-form.config';
import { Roles } from '@core/constants/security/roles.constants';
import { useAuth } from '@core/utils/context/auth-context';
import { useAdminContext } from '../../context/admin.context';

export default function UserDetails({ userId }) {
    const formRef = useRef(null);

    // Roles come from Entra group membership (role sync), so the role list is read-only for
    // everyone except Developers, who can override it by hand - e.g. for locally-created test
    // accounts, which the Entra sync leaves alone. The server enforces the same rule
    // (UpdateUserCommandHandler ignores role changes from anyone else).
    const { isInRole } = useAuth();
    const canEditRoles = Boolean(isInRole(Roles.Developer));

    const formOptions = useMemo(
        () => ({
            ...UserDetailsFormOptions,
            layout: {
                ...UserDetailsFormOptions.layout,
                roles: { ...UserDetailsFormOptions.layout.roles, disabled: !canEditRoles },
            },
        }),
        [canEditRoles]
    );

    const queryClient = useQueryClient();
    const queryKey = ['/admin/users/' + userId, {}];

    const { handleUsersUpdate } = useAdminContext();

    const { data: userData, isLoading } = useCachedFetch(
        `/admin/users/${userId}`,
        {},
        { enabled: !!userId }
    );

    const { put, cancel, isSubmitting } = usePut(`/admin/users/${userId}`);

    const updateUser = useMutation({
        mutationFn: async (payload) => (await put(payload)).data,
        onSuccess: (response) => {
            if (response) {
                queryClient.setQueryData(queryKey, response);
                handleUsersUpdate(response);
            }
        },
        onSettled: () => {
            queryClient.invalidateQueries({ queryKey });
        },
    });

    const handleSave = async (data) => {
        try {
            await updateUser.mutateAsync({ ...data });
        } catch (error) {
            console.error(error);
            throw error;
        }
    };

    const handleReset = (event) => {
        if (isSubmitting) {
            cancel();
        } else {
            formRef?.current?.reset(event);
        }
    };

    const handleSubmit = (event) => {
        formRef?.current?.handleSubmit(event);
    };

    if (isLoading) {
        return html`<${LoaderWithTitle} busyLoading=${true} title="Loading user details..." />`;
    }

    return userData
        ? html`
              <${Form}
                  ref=${formRef}
                  initialValues=${userData}
                  onSubmit=${handleSave}
                  schema=${UserDetailsFormSchema}
                  options=${formOptions}
              />

              <div class="card mt-2">
                  <div class="card-footer">
                      <${Button}
                          buttonType="secondary"
                          label=${{ text: 'Reset' }}
                          onClick=${handleReset}
                      />
                      <${Button}
                          buttonType="primary"
                          label=${{ text: isSubmitting ? 'Saving...' : 'Save' }}
                          disabled=${isSubmitting}
                          onClick=${handleSubmit}
                      />
                  </div>
              </div>
          `
        : null;
}
